import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";


export default function HomePage() {
  return (
    <PublicLayout>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up { animation: fadeUp 0.7s ease both; }
        .delay-1 { animation-delay: 0.1s; }
        .delay-2 { animation-delay: 0.2s; }
        .delay-3 { animation-delay: 0.35s; }
        .hero-pattern {
          background-image:
            radial-gradient(circle at 20% 50%, rgba(249,115,22,0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(6,182,212,0.10) 0%, transparent 50%),
            radial-gradient(circle at 50% 80%, rgba(249,115,22,0.08) 0%, transparent 50%);
        }
        .grid-lines {
          background-image:
            linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px);
          background-size: 60px 60px;
        }
      `}</style>


      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center bg-slate-900 hero-pattern overflow-hidden">
        <div className="absolute inset-0 opacity-5 grid-lines" />


        <div className="relative max-w-6xl mx-auto px-5 pt-28 pb-20 text-center">


          {/* Badge */}
          <div className="animate-fade-up inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-8">
            <span className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse" />
            Built for Kenyan Businesses
          </div>


          {/* Headline */}
          <h1
            className="animate-fade-up delay-1 text-5xl md:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Deliver Smarter
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
              Across Kenya
            </span>
          </h1>


          <p className="animate-fade-up delay-2 text-slate-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            TumaTrack gives shops, couriers, and supermarkets a real-time window
            into every delivery — from dispatch to doorstep, on any vehicle.
          </p>


          {/* CTA Buttons */}
          <div className="animate-fade-up delay-3 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold px-8 py-3.5 rounded-xl shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-200 hover:-translate-y-0.5"
            >
              Start Free 7-Day Trial
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
            <Link
              to="/pricing"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium px-8 py-3.5 rounded-xl transition-all duration-200"
            >
              See Pricing
            </Link>
          </div>


          {/* Trust Badges */}
          <div className="mt-14 flex flex-wrap justify-center gap-x-8 gap-y-3 text-slate-500 text-sm">
            {["No credit card required", "Cancel anytime", "M-Pesa payments accepted"].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                {t}
              </span>
            ))}
          </div>
        </div>


        {/* Bottom fade to white */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
      </section>


      {/* ── STATS BAR ── */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: "6",         label: "Vehicle types supported" },
            { value: "Real-time", label: "GPS tracking" },
            { value: "7 days",   label: "Free trial" },
            { value: "KES 999",   label: "Starting price / month" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-3xl font-extrabold text-slate-900 mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>
                {s.value}
              </p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      </section>


      {/* ── FEATURES ── */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-16">
            <p className="text-orange-500 font-semibold text-sm uppercase tracking-widest mb-3">Features</p>
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
              Everything a delivery business needs
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto">
              One platform for your dispatch team, drivers, and customers —
              no spreadsheets, no WhatsApp chaos.
            </p>
          </div>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((f) => (
              <div
                key={f.title}
                className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${f.iconBg}`}>
                  <span className="text-2xl">{f.icon}</span>
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                  {f.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── HOW IT WORKS ── */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-16">
            <p className="text-orange-500 font-semibold text-sm uppercase tracking-widest mb-3">How It Works</p>
            <h2 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "'Sora', sans-serif" }}>
              Up and running in minutes
            </h2>
          </div>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {steps.map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-orange-200">
                  <span className="text-3xl">{step.icon}</span>
                </div>
                <div className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-1">Step {i + 1}</div>
                <h3 className="font-bold text-slate-900 text-lg mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                  {step.title}
                </h3>
                <p className="text-slate-500 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── VEHICLE TYPES ── */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-6xl mx-auto px-5 text-center">
          <p className="text-slate-400 text-sm mb-6">Supports any vehicle your business runs</p>
          <div className="flex flex-wrap justify-center gap-3">
            {["🏍️ Motorcycle", "🚲 Bicycle", "🚐 Van", "🚛 Truck", "🚌 Bus", "🚚 Lorry"].map((v) => (
              <span key={v} className="bg-slate-800 border border-slate-700 text-slate-300 text-sm font-medium px-5 py-2.5 rounded-full">
                {v}
              </span>
            ))}
          </div>
        </div>
      </section>


      {/* ── FINAL CTA ── */}
      <section className="py-24 bg-gradient-to-br from-orange-500 to-amber-500">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="text-4xl font-extrabold text-white mb-5" style={{ fontFamily: "'Sora', sans-serif" }}>
            Ready to tuma smarter?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Join businesses across Kenya already tracking deliveries in real time. Free for 7 days.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-white text-orange-600 font-bold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
          >
            Create Your Free Account
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}


const features = [
  { icon: "📍", iconBg: "bg-orange-50",  title: "Live GPS Tracking",       desc: "See exactly where every driver is on a live Leaflet map. Updates every 30 seconds automatically." },
  { icon: "🔔", iconBg: "bg-blue-50",    title: "Real-Time Updates",       desc: "Instant notifications when orders change status — no page refreshes needed." },
  { icon: "🔗", iconBg: "bg-green-50",   title: "Customer Tracking Link",  desc: "Auto-generated tracking link for every order. Share with customers via WhatsApp or SMS." },
  { icon: "📦", iconBg: "bg-purple-50",  title: "Full Order Lifecycle",    desc: "From placed → assigned → picked up → on the way → delivered. Every step logged with timestamps." },
  { icon: "👥", iconBg: "bg-amber-50",   title: "Built for Multiple Businesses",       desc: "Each business's data is completely separate and secure. One platform, many businesses." },
  { icon: "📊", iconBg: "bg-rose-50",    title: "Driver History",          desc: "Daily, weekly, and monthly trip summaries. Know which drivers are performing." },
];


const steps = [
  { icon: "🏪", title: "Register Your Shop",    desc: "Sign up in under 2 minutes and get your 7-day free trial instantly." },
  { icon: "🏍️", title: "Add Your Drivers",      desc: "Create driver accounts. They log in on the mobile app on their phones." },
  { icon: "🗺️", title: "Track Every Delivery",  desc: "Assign orders, watch them move on the map, share tracking links with customers." },
];